import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  Heart,
  Package,
  Truck,
  AlertTriangle,
  FileText,
  TrendingUp,
  Phone,
  MessageCircle,
  Calendar,
  ChevronRight,
  Megaphone,
  CalendarDays,
  MapPin,
  Clock,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TIPI_DOCUMENTO_LABELS = {
  carta_identita: "Carta d'identita",
  codice_fiscale: 'Codice fiscale',
  stato_famiglia: 'Stato di famiglia',
  isee: 'ISEE',
  permesso_soggiorno: 'Permesso di soggiorno',
  documenti_sanitari: 'Documenti sanitari',
  domanda_banco: 'Domanda banco alimentare',
  consenso_privacy: 'Consenso privacy',
};

const StatCard = ({ title, value, icon: Icon, color, description, onClick }) => (
  <Card 
    data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`} 
    className={onClick ? 'cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98]' : ''}
    onClick={onClick}
  >
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-center gap-3 sm:gap-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-muted-foreground truncate">{title}</p>
          <p className="text-xl sm:text-2xl font-heading font-bold">{value}</p>
          {description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{description}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const buildWhatsAppMessage = (doc) => {
  const nome = doc.famiglia_nome || '';
  const dataScadenza = new Date(doc.scadenza).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  const tipoDoc = TIPI_DOCUMENTO_LABELS[doc.tipo_documento] || doc.tipo_documento;
  const chiesa = doc.chiesa_nome || 'la nostra chiesa';
  
  return `Ciao caro ${nome} spero che stai bene?!\n\nTi ricordiamo che in data ${dataScadenza} scadra' il documento ${tipoDoc}, ti ricordiamo per continuare ad aderire al nostro banco alimentare ${chiesa} avrai bisogno di ripresentare questo documento prima della scadenza ogni anno.\n\nPer tanto, scrivimi e gestiamo insieme la pratica.\nUn saluto Dio ti benedica`;
};

const buildWhatsAppUrl = (phone, message) => {
  const cleanPhone = phone.replace(/[^0-9+]/g, '');
  const intlPhone = cleanPhone.startsWith('+') ? cleanPhone : (cleanPhone.startsWith('0') ? '+39' + cleanPhone.substring(1) : '+39' + cleanPhone);
  return `https://wa.me/${intlPhone.replace('+', '')}?text=${encodeURIComponent(message)}`;
};

const DocScadenzaCard = ({ doc, navigate }) => {
  const giorni = doc.giorni_rimanenti;
  const isExpired = giorni < 0;
  const isUrgent = giorni <= 7;
  const tipoLabel = TIPI_DOCUMENTO_LABELS[doc.tipo_documento] || doc.tipo_documento;
  const dataScadenza = new Date(doc.scadenza).toLocaleDateString('it-IT');
  const whatsappMessage = buildWhatsAppMessage(doc);

  return (
    <div className="p-3 sm:p-4 border-b border-border/40 last:border-b-0" data-testid={`doc-scadenza-${doc.id}`}>
      <div className="flex items-start gap-3">
        {/* Status indicator */}
        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isExpired ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : 'bg-orange-400'}`} />
        
        <div className="flex-1 min-w-0">
          {/* Name and document type */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{doc.famiglia_nome}</span>
            <Badge variant={isExpired ? 'destructive' : isUrgent ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0">
              {isExpired ? `Scaduto da ${Math.abs(giorni)}g` : giorni === 0 ? 'Scade oggi' : `${giorni}g rimasti`}
            </Badge>
          </div>
          
          {/* Document type and date */}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {tipoLabel}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {dataScadenza}
            </span>
          </div>
          
          {/* Phone + WhatsApp */}
          {doc.telefono && (
            <div className="flex items-center gap-2 mt-2">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Phone className="w-3 h-3" />
                {doc.telefono}
              </span>
              <a
                href={buildWhatsAppUrl(doc.telefono, whatsappMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                data-testid={`whatsapp-doc-${doc.id}`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                Invia Sollecito WhatsApp
              </a>
            </div>
          )}
        </div>

        {/* Navigate to family */}
        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => navigate(`/famiglie/${doc.famiglia_id}`)} data-testid={`goto-famiglia-${doc.famiglia_id}`}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [docScadenza, setDocScadenza] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [statsRes, docsRes, newsRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/dashboard/documenti-scadenza`),
        axios.get(`${API}/news`)
      ]);
      setStats(statsRes.data);
      setDocScadenza(docsRes.data);
      setNews(newsRes.data.slice(0, 5));
    } catch (err) {
      toast.error('Errore nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 sm:h-32" />
          ))}
        </div>
        <Skeleton className="h-48 sm:h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="hidden sm:block">
        <h1 className="text-2xl font-heading font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Riepilogo dell'attivita del banco alimentare</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard title="Famiglie Attive" value={stats?.famiglie_attive || 0} icon={Users} color="bg-primary/10 text-primary" onClick={() => navigate('/famiglie')} />
        <StatCard title="Persone Assistite" value={stats?.persone_assistite || 0} icon={Heart} color="bg-red-500/10 text-red-500" onClick={() => navigate('/famiglie')} />
        <StatCard title="Donazioni Mese" value={stats?.donazioni_mese || 0} icon={Package} color="bg-green-600/10 text-green-600" onClick={() => navigate('/donazioni')} />
        <StatCard title="Valore Distribuito" value={`${(stats?.valore_distribuito_mese || 0).toFixed(0)}`} icon={TrendingUp} color="bg-amber-500/10 text-amber-500" description="questo mese" onClick={() => navigate('/distribuzioni')} />
      </div>

      {/* Alerts Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
        <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => navigate('/magazzino')}>
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              <CardTitle className="text-sm sm:text-base">Prodotti Sotto Scorta</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {stats?.prodotti_esaurimento > 0 ? (
              <div className="flex items-center gap-3">
                <Badge variant="destructive" className="text-base sm:text-lg px-3 py-1">{stats.prodotti_esaurimento}</Badge>
                <span className="text-xs sm:text-sm text-muted-foreground">prodotti da reintegrare</span>
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-muted-foreground">Nessun prodotto sotto scorta</p>
            )}
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => navigate('/magazzino')}>
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
              <CardTitle className="text-sm sm:text-base">Prodotti in Scadenza</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {stats?.prodotti_scadenza > 0 ? (
              <div className="flex items-center gap-3">
                <Badge variant="destructive" className="text-base sm:text-lg px-3 py-1">{stats.prodotti_scadenza}</Badge>
                <span className="text-xs sm:text-sm text-muted-foreground">entro 7 giorni</span>
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-muted-foreground">Nessun prodotto in scadenza</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Documenti in Scadenza - DETAILED SECTION */}
      <Card className={docScadenza.length > 0 ? 'border-orange-300 dark:border-orange-700' : ''}>
        <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 sm:pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
              <CardTitle className="text-sm sm:text-base">Documenti in Scadenza</CardTitle>
            </div>
            {docScadenza.length > 0 && (
              <Badge variant="destructive" className="text-sm px-2.5 py-0.5">
                {docScadenza.length}
              </Badge>
            )}
          </div>
          {docScadenza.length > 0 && (
            <CardDescription className="text-xs mt-1">
              Clicca su "Invia Sollecito WhatsApp" per inviare un promemoria automatico alla famiglia
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {docScadenza.length === 0 ? (
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              <p className="text-xs sm:text-sm text-muted-foreground">Tutti i documenti sono in regola</p>
            </div>
          ) : (
            <div data-testid="documenti-scadenza-list">
              {docScadenza.map((doc) => (
                <DocScadenzaCard key={doc.id} doc={doc} navigate={navigate} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* News Widget + Quick summary - Side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-6">
        {/* News Widget - Compact */}
        <Card className="lg:col-span-2" data-testid="news-widget">
          <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 sm:pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <CardTitle className="text-sm sm:text-base">Comunicazioni</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/news')} data-testid="news-widget-vedi-tutte">
                Vedi tutte <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {news.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Nessuna comunicazione</p>
            ) : (
              <div className="space-y-2">
                {news.slice(0, 4).map(item => {
                  const tipoIcon = { avviso: AlertCircle, aggiornamento: Info, evento: CalendarDays };
                  const tipoColor = { avviso: 'text-amber-500', aggiornamento: 'text-blue-500', evento: 'text-green-500' };
                  const NIcon = tipoIcon[item.tipo] || AlertCircle;
                  return (
                    <div key={item.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate('/news')}>
                      <NIcon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${tipoColor[item.tipo] || 'text-muted-foreground'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate">{item.titolo}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{item.contenuto}</p>
                        {item.tipo === 'evento' && item.data_evento && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <CalendarDays className="w-2.5 h-2.5 text-green-500" />
                            <span className="text-[10px] text-green-600">{item.data_evento}{item.ora_evento ? ` ${item.ora_evento}` : ''}</span>
                          </div>
                        )}
                      </div>
                      {item.priorita === 'alta' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick summary */}
        <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/20 lg:col-span-3">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-heading font-bold">Riepilogo Attivita</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                  Il banco alimentare attualmente assiste <strong>{stats?.famiglie_attive || 0} famiglie</strong> per 
                  un totale di <strong>{stats?.persone_assistite || 0} persone</strong>. 
                  Questo mese sono state registrate <strong>{stats?.donazioni_mese || 0} donazioni</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
